<script setup lang="ts">
import { computed } from 'vue'
import { useAppMode } from '@/composables/useAppMode'
import { cn } from '@/lib/utils'
import { percentToColor } from '@/lib/converters'

import DrillItemManageMenu from '@/components/drills/list/DrillItemManageMenu.vue'
import DrillItemChip from '@/components/drills/list/DrillItemChip.vue'
import PlaySvg from '@/assets/icons/play.svg'
import CardsSvg from '@/assets/icons/cards.svg'

import { DrillManageActionType } from '@/types'
import { Street } from '@acepoker/contract'
import type { DrillWithStatistic, DrillSequence } from '@acepoker/contract'

const props = defineProps<{
  drillData: DrillWithStatistic
  drillClass?: string
  editing?: boolean
  selected?: boolean
}>()

defineEmits<{
  onManageActionSelect: [DrillManageActionType]
}>()

const { mode, windowWidth } = useAppMode()
const isDesktopDrill = computed(() => mode.value === 'desktop')

const drillStreet = computed(() => {
  return (
    props.drillData.Drill.data?.street || 'Custom'
  )
})

function getStreetFromSequence(sequence: DrillSequence['sequence']): string {
  return sequence?.findLast((item) => item.kind === 'round')?.round || Street.Preflop
}
</script>

<template>
  <article
    :class="
      cn(
        'group bg-background-card hover:bg-background-card-hover flex cursor-pointer flex-col gap-2 rounded-lg border border-white/5 p-3',
        'has-[.menu-expanded]:bg-1 has-[.menu-expanded]:border-light-grey',
        'data-[selected=true]:bg-primary-button',
        drillClass
      )
    "
    :data-selected="selected"
  >
    <header class="flex min-h-[26px] items-center justify-between text-white">
      <span class="text-custom-17 truncate font-medium">{{
        drillData.Drill.name || $t('NLHDrillsPage.label.PokerBasics')
      }}</span>
      <div v-if="editing">
        <DrillItemManageMenu
          :class="{
            '[&>button>svg]:text-2 [&>button:hover>svg]:opacity-80': selected
          }"
          :visible-actions="[
            DrillManageActionType.edit,
            DrillManageActionType.resetStats,
            DrillManageActionType.delete
          ]"
          @on-action-select="(val) => $emit('onManageActionSelect', val)"
        />
      </div>
    </header>

    <div
      class="flex gap-1"
      v-if="
        (drillData.PlayerStatistic &&
          (drillData.PlayerStatistic.handsTotal !== 0 || drillData.PlayerStatistic.score !== 0)) ||
        (drillData.PlayerHandHistory && drillData.PlayerHandHistory.totalMoves !== 0)
      "
    >
      <DrillItemChip
        v-if="drillData.PlayerStatistic && drillData.PlayerStatistic.handsTotal !== 0"
        :label="$t('NLHDrillsPage.label.Hands')"
        :value="drillData.PlayerStatistic.handsTotal"
      />

      <div class="grow" />

      <DrillItemChip
        v-if="drillData.PlayerHandHistory && drillData.PlayerHandHistory.totalMoves !== 0"
        :label="$t('NLHDrillsPage.label.Rate')"
        :value="`${(
          (drillData.PlayerHandHistory.correctMoves / drillData.PlayerHandHistory.totalMoves) *
          100
        ).toFixed()}%`"
        :style="{
          backgroundColor: percentToColor(
            (drillData.PlayerHandHistory.correctMoves / drillData.PlayerHandHistory.totalMoves) *
              100
          )
        }"
      />
    </div>

    <div
      class="flex gap-2 rounded-md bg-[#070E1E]/75 px-2 py-0.5 text-xs text-white/60 group-data-[selected=true]:text-white"
      v-if="drillStreet || drillData.Drill.gaming_configuration"
    >
      <div v-if="drillStreet" class="flex items-center gap-1 capitalize">
        <PlaySvg class="w-3" />
        <span>{{ drillStreet }}</span>
      </div>
      <div class="flex items-center gap-1" v-if="drillData.Drill.gaming_configuration">
        <CardsSvg />
        <span>{{ drillData.Drill.gaming_configuration.game }}</span>
        <span>{{ `${drillData.Drill.max_players}-Max` }}</span>
      </div>
    </div>
  </article>
</template>

<style lang="scss" scoped></style>
